/**
 * TanStack Query hooks for the notifications domain (M4.4 endpoints).
 *
 * Key factory: ["notifications", ...] — all notification and preference caches
 * are namespaced here.
 *
 * Polling strategy:
 *   - useUnreadCountQuery: refetchInterval 15 s — drives the topbar bell badge.
 *   - useNotificationsQuery: refetchInterval 30 s — drives the bell dropdown list.
 *   - usePreferencesQuery: staleTime 5 min — infrequently-changing data.
 *
 * Mutations (all PESSIMISTIC):
 *   - useMarkReadMutation:
 *       invalidates notifications list + unread count on success.
 *       toast.success "Marked as read".
 *   - useUpdatePreferencesMutation:
 *       invalidates preferences on success.
 *       toast.success "Preferences saved."
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  listNotifications,
  getUnreadCount,
  markRead,
  getPreferences,
  updatePreferences,
  type NotificationListQuery,
  type NotificationListResponse,
  type UnreadCountResponse,
  type MarkReadRequest,
  type MarkReadResponse,
  type NotificationPreferenceResponse,
  type NotificationPreferenceUpdateRequest,
} from '@/lib/api/notifications';
import { humanizeError } from '@/lib/utils/http-errors';

// ── Query key factory ─────────────────────────────────────────────────────────

/**
 * Canonical key factory for the notifications domain.
 * Exported so components and other mutations can cross-invalidate.
 */
export const notificationsQueryKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationsQueryKeys.all, 'list'] as const,
  list: (q: Partial<NotificationListQuery>) =>
    [...notificationsQueryKeys.lists(), q] as const,
  unreadCount: () => [...notificationsQueryKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationsQueryKeys.all, 'preferences'] as const,
} as const;

// ── Query hooks ───────────────────────────────────────────────────────────────

/**
 * List notifications for the current user with optional filters.
 * staleTime 15 s; refetchInterval 30 s for live bell dropdown updates.
 */
export function useNotificationsQuery(
  query: Partial<NotificationListQuery>
): UseQueryResult<NotificationListResponse, AxiosError> {
  return useQuery<NotificationListResponse, AxiosError>({
    queryKey: notificationsQueryKeys.list(query),
    queryFn: () => listNotifications(query),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Lightweight unread-count poll — used exclusively by the topbar bell badge.
 * staleTime 10 s; refetchInterval 15 s (faster than the full list).
 */
export function useUnreadCountQuery(): UseQueryResult<UnreadCountResponse, AxiosError> {
  return useQuery<UnreadCountResponse, AxiosError>({
    queryKey: notificationsQueryKeys.unreadCount(),
    queryFn: getUnreadCount,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

/**
 * Current user's notification preferences.
 * staleTime 5 min — preferences change infrequently; only invalidated on update.
 */
export function usePreferencesQuery(): UseQueryResult<NotificationPreferenceResponse, AxiosError> {
  return useQuery<NotificationPreferenceResponse, AxiosError>({
    queryKey: notificationsQueryKeys.preferences(),
    queryFn: getPreferences,
    staleTime: 5 * 60_000,
  });
}

// ── Mutation hooks ────────────────────────────────────────────────────────────

/**
 * Mark one or more notifications as read.
 *
 * PESSIMISTIC: the server validates ownership and returns skipped IDs.
 *
 * onSuccess:
 *   - Invalidates notifications list (all filters) so read indicators update.
 *   - Invalidates unread count so the bell badge decrements.
 *   - toast.success "Marked as read".
 */
export function useMarkReadMutation(): UseMutationResult<
  MarkReadResponse,
  AxiosError,
  MarkReadRequest
> {
  const queryClient = useQueryClient();

  return useMutation<MarkReadResponse, AxiosError, MarkReadRequest>({
    mutationFn: markRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.unreadCount() });
      toast.success('Marked as read');
    },
    onError: (err) => {
      toast.error(humanizeError(err));
    },
  });
}

/**
 * Update the current user's notification preferences.
 *
 * PESSIMISTIC: server validates quiet-hours logic and persists atomically.
 *
 * onSuccess:
 *   - Invalidates the preferences cache so the form re-hydrates.
 *   - toast.success "Preferences saved."
 */
export function useUpdatePreferencesMutation(): UseMutationResult<
  NotificationPreferenceResponse,
  AxiosError,
  NotificationPreferenceUpdateRequest
> {
  const queryClient = useQueryClient();

  return useMutation<NotificationPreferenceResponse, AxiosError, NotificationPreferenceUpdateRequest>({
    mutationFn: updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.preferences() });
      toast.success('Preferences saved.');
    },
    onError: (err) => {
      toast.error(humanizeError(err));
    },
  });
}
